import { EditTwoTone, InboxOutlined, PlusOutlined } from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Popconfirm,
    Skeleton,
    Typography,
    Upload,
} from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import { decrypt, formatDate, getBase64 } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
const { Dragger } = Upload;

function RoomsAvailable() {
    const [form] = Form.useForm();
    const [fileForm] = Form.useForm();

    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [selectedDate, setSelectedDate] = useState(
        moment().subtract(1, "day"),
    );
    let fileObj = null;
    const [uploadDate, setUploadDate] = useState(null);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [formData, setFromData] = useState({
        staff_id,
        trans_date: formatDate(selectedDate),
    });
    const [tableData, setTableData] = useState([]);
    const [editingKey, setEditingKey] = useState("");

    const columns = [
        {
            title: "Hotel Id",
            dataIndex: "hotel_id",
        },
        {
            title: "Hotel Name",
            dataIndex: "hotel_name",
        },
        {
            title: "City",
            dataIndex: "city",
        },
        {
            title: "Rooms Sold",
            dataIndex: "rooms_sold",
            editable: true,
        },
        {
            title: "Out Of Order",
            dataIndex: "out_of_order",
            editable: true,
        },
        {
            title: "Adoption (%)",
            dataIndex: "adopt_percentage",
        },
        {
            title: "Occupancy (%)",
            dataIndex: "occ_percentage",
            editable: true,
        },
        {
            title: "Action",
            render: (_, record) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <Typography.Link
                            onClick={() => save(record.hotel_id)}
                            style={{
                                marginRight: 8,
                            }}
                        >
                            Save
                        </Typography.Link>
                        <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                            <a>Cancel</a>
                        </Popconfirm>
                    </span>
                ) : (
                    <>
                        {editingKey !== "" ? (
                            <EditTwoTone
                                className="actionIcon"
                                twoToneColor="grey"
                            />
                        ) : (
                            <EditTwoTone
                                className="actionIcon"
                                onClick={() => edit(record)}
                            />
                        )}
                    </>
                );
            },
        },
    ];

    const {
        status: roomDetailStatus,
        data: roomDetailsRes,
        refetch: getRoomDetails,
    } = useQuery(
        "rooms-details",
        async () => {
            return HTTP("/rooms/getRoomsSold", formData);
        },
        { enabled: false },
    );

    const { data: tokenData, refetch: getToken } = useQuery(
        "randomToken",
        async () => {
            return await HTTP("/dashboard/getRandomToken", { staff_id });
        },
        {
            enabled: false,
            select: (data) => {
                if (data.success && Array.isArray(data.data)) {
                    return decrypt(data.data[0]?.random_string);
                } else {
                    return null;
                }
            },
        },
    );

    useEffect(() => {
        form.setFieldsValue({ trans_date: moment().subtract(1, "day") });
        getRoomDetails();
        getToken();
    }, []);

    useEffect(() => {
        getRoomDetails();
    }, [formData]);

    useEffect(() => {
        if (roomDetailStatus === "success") {
            setTableData(roomDetailsRes.data);
        }
    }, [roomDetailStatus, roomDetailsRes]);

    function disabledDate(current) {
        return current && current > moment().subtract(1, "day").endOf("day");
    }

    function openUploadPopup() {
        setUploadModalVisible(true);
    }

    function handleUploadModalClose() {
        fileObj = null;
        fileForm.resetFields();
        setUploadDate(null);
        setUploadModalVisible(false);
        getRoomDetails();
    }

    function edit(record) {
        form.setFieldsValue({
            ...record,
        });
        setEditingKey(record.hotel_id);
    }

    function isEditing(record) {
        return record.hotel_id === editingKey;
    }

    function cancel() {
        setEditingKey("");
    }

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                inputType: col.dataIndex === "age" ? "number" : "text",
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    function handleDateChange(date, dateString) {
        setSelectedDate(moment(dateString));
        setFromData({ ...formData, trans_date: formatDate(dateString) });
    }

    const EditableCell = ({
        editing,
        dataIndex,
        title,
        inputType,
        record,
        index,
        children,
        ...restProps
    }) => {
        const inputNode = inputType === "number" ? <InputNumber /> : <Input />;
        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{
                            margin: 0,
                        }}
                        rules={[
                            {
                                required: true,
                                message: `Please Input ${title}!`,
                            },
                        ]}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        );
    };

    async function save(hotel_id) {
        const row = { ...(await form.validateFields()), hotel_id };
        const newData = [...roomDetailsRes.data];
        const index = newData.findIndex((item) => hotel_id === item.hotel_id);
        const { success, message } = await HTTP("/rooms/addRoomsSold", {
            staff_id,
            rooms_details: [row],
            trans_date: formatDate(selectedDate),
        });

        if (success === 0) {
            notification["error"]({
                message: message,
            });
            setEditingKey("");
        } else {
            notification["info"]({
                message: message,
            });
            const item = newData[index];
            newData.splice(index, 1, { ...item, ...{ ...row, hotel_id } });
            setTableData(newData);
            setEditingKey("");
        }
    }

    async function uploadDetails() {
        fileForm.validateFields().then((values) => {
            getBase64(fileObj.file.originFileObj, async (dataUrl) => {
                const { success, data, message } = await HTTP(
                    "/rooms/reportUploadFileNew",
                    {
                        staff_id,
                        trans_date: formatDate(uploadDate),
                        file_path: dataUrl,
                        file_name: fileObj.file.name,
                    },
                );
                let notification_type = "info";
                if (success === 0) {
                    notification_type = "error";
                }
                notification[notification_type]({
                    message: message,
                });
                handleUploadModalClose();
            });
        });
    }

    function handleFileUpload(info) {
        fileObj = info;
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            <Card
                className="box-shadow margin-10"
                title={
                    <div className="display-flex flex-space-between">
                        <div
                            style={{ alignItems: "center" }}
                            className="display-flex"
                        >
                            <span>{`Token : ${tokenData || ""}`}</span>
                        </div>
                        <div>
                            <DatePicker
                                allowClear={false}
                                className="margin-10"
                                defaultValue={moment().subtract(1, "day")}
                                value={selectedDate}
                                onChange={handleDateChange}
                                format={"MM/DD/YYYY"}
                                disabledDate={disabledDate}
                                showToday={false}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openUploadPopup}
                            >
                                Upload Room Details
                            </Button>
                        </div>
                    </div>
                }
            >
                <Form form={form} component={false}>
                    {roomDetailStatus != "idle" &&
                    roomDetailStatus != "loading" ? (
                        <CustomTable
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                            rowKey="hotel_id"
                            columns={mergedColumns}
                            tableData={tableData}
                        />
                    ) : (
                        <Skeleton
                            active={true}
                            title={false}
                            paragraph={{ rows: 10 }}
                        />
                    )}
                </Form>
            </Card>
            <Modal
                open={uploadModalVisible}
                footer={
                    <div className="margin-10">
                        <Button onClick={handleUploadModalClose}>Cancel</Button>
                        <Button type="primary" onClick={uploadDetails}>
                            Submit
                        </Button>
                    </div>
                }
                title="Upload Details"
                destroyOnClose={true}
                onCancel={handleUploadModalClose}
            >
                <Form form={fileForm}>
                    <Form.Item name="Date" rules={[{ required: true }]}>
                        <DatePicker
                            allowClear={false}
                            value={uploadDate}
                            format={"MM/DD/YYYY"}
                            onChange={(date, dateString) => {
                                setUploadDate(date);
                            }}
                            disabledDate={(current) => {
                                return (
                                    current &&
                                    current > moment().subtract(1, "day")
                                );
                            }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Dragger
                            beforeUpload={() => false}
                            rules={[
                                {
                                    required: true,
                                },
                            ]}
                            id="file"
                            name="file"
                            onChange={(info) => handleFileUpload(info)}
                            accept=".csv, .xlsx"
                            maxCount={1}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag file to this area to upload
                            </p>
                        </Dragger>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
}
export { RoomsAvailable };
